import { useTranslate, useMany } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Space, Input, Tag, Form, Row, Col } from 'antd';
import { LinkOutlined, SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { ProfileOut, UserOut } from '@/api/generated/schemas';
import { ProfileRole } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import type { CrudFilter } from '@refinedev/core';
import { SelectFilter } from '@/components/common/SelectFilter';
import { TextFilter } from '@/components/common/TextFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

const roleColors: Record<string, string> = {
  owner: 'gold',
  admin: 'blue',
  member: 'default',
};

export const ProfileList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<ProfileOut>({
      pagination: { pageSize: 20 },
      resource: 'account.profiles',
      onSearch,
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const profileData = (tableProps.dataSource ?? []) as ProfileOut[];
  const userIds = [...new Set(profileData.map((p) => p.user_id))];

  const { result: usersResult } = useMany<UserOut>({
    resource: 'iam.users',
    ids: userIds,
    queryOptions: { enabled: userIds.length > 0 },
  });

  const usersById = Object.fromEntries(
    (usersResult?.data ?? []).map((u: UserOut) => [u.id, u])
  );

  return (
    <List canCreate={false}>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('account.profiles.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table {...rowNav('account.profiles', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="user_id"
          title={t('account.profiles.fields.user_id')}
          render={(userId: number) => {
            const user = usersById[userId];

            return (
              <Space size={8}>
                <span>{user ? (user.email ?? user.username) : userId}</span>
                <Link to={`/iam/users/${userId}`}>
                  <LinkOutlined />
                </Link>
              </Space>
            );
          }}
          filtered={isFieldFiltered(filters as CrudFilter[], 'user_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="user_id"
              placeholder={t('account.profiles.fields.user_id')}
              width={120}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="account_id"
          title={t('account.profiles.fields.account_id')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'account_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="account_id"
              placeholder={t('account.profiles.fields.account_id')}
              width={120}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="name"
          title={t('account.profiles.fields.name')}
          render={(value: string | null) => value ?? '-'}
        />

        <Table.Column
          dataIndex="role"
          title={t('account.profiles.fields.role')}
          render={(value: string) => (
            <Tag color={roleColors[value] ?? 'default'}>
              {value ? t(`account.profiles.role.${value}`) : '-'}
            </Tag>
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'role')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="role"
              minWidth={160}
              placeholder={t('account.profiles.fields.role')}
              options={Object.values(ProfileRole).map((value) => ({
                label: t(`account.profiles.role.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
